import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, FileText, Download, DoorOpen } from 'lucide-react';
import { toast } from 'sonner';
import { OnboardingBackButton } from '../OnboardingBackButton';
import { reflowAgreementBody } from '../../utils/reflowAgreementBody';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Re-usable agreement screen used by both the venue onboarding flow
 * and the host listing flow. Fetches the current rendered agreement
 * from the backend, shows it inside a scrollable card, and exposes a
 * single "I agree" checkbox + action button.
 *
 * Parent passes:
 *  - kind: 'venue' | 'host'
 *  - actionLabel: text for the primary CTA when ready to submit
 *  - onAccept: async (versionString) => Promise<void> — does the actual
 *    submission + agreement POST. We hand the version we displayed so
 *    callers can record exactly what the user accepted.
 *  - busy: when true, disables the CTA (parent is mid-submit).
 *  - testIdPrefix: e.g. 'venue-agreement' so e2e tests get stable hooks.
 */
export default function LegalAgreementView({
  kind,
  actionLabel = 'Submit',
  onAccept,
  onBack,
  busy = false,
  testIdPrefix = 'agreement',
  title,
  subtitle,
  signerBlock = null,
}) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [englishOk, setEnglishOk] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/legal/agreement-template`, {
          params: { kind },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setTemplate(res.data);
      } catch (_err) {
        if (!cancelled) setError('Could not load the agreement. Please check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const heading = title || (kind === 'venue' ? 'Venue Partner Agreement' : 'Host Listing Agreement');
  const blurb = subtitle || (kind === 'venue'
    ? 'Please review the agreement below. You only need to tick "I agree" — a signed PDF copy is kept in your dashboard.'
    : 'Please review the agreement below. You only need to tick "I agree" — a signed PDF copy is kept in your dashboard.');

  const canSubmit = useMemo(() => accepted && englishOk && !busy && template, [accepted, englishOk, busy, template]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      // Pass both affirmations so the parent's onAccept can forward them
      // to recordAgreement — parents that don't care can ignore them.
      await onAccept(template.version, {
        accepted_agreement: accepted,
        accepted_english_comprehension: englishOk,
      });
    } catch (err) {
      console.error('Agreement submit failed:', err);
    }
  };

  return (
    <div
      data-testid={`${testIdPrefix}-screen`}
      style={{
        minHeight: '100dvh',
        background: 'var(--app-bg)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: 'calc(env(safe-area-inset-top, 0px) + 22px) 20px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          alignItems: 'flex-start',
        }}
      >
        {onBack ? (
          <div style={{ marginBottom: 12 }}>
            <OnboardingBackButton
              onClick={onBack}
              testId={`${testIdPrefix}-back`}
              disabled={busy}
            />
          </div>
        ) : null}
        <span
          data-testid={`${testIdPrefix}-pill`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            borderRadius: 999,
            background: 'var(--accent-surface-soft)',
            color: 'var(--accent-purple)',
            fontFamily: "'Inria Serif', serif",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <ShieldCheck size={12} strokeWidth={2.2} />
          Final step — Legal
        </span>
        <h1
          data-testid={`${testIdPrefix}-title`}
          style={{
            margin: '6px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 26,
            lineHeight: 1.18,
            fontWeight: 700,
            color: 'var(--text)',
            letterSpacing: '-0.01em',
          }}
        >
          {heading}
        </h1>
        <p
          data-testid={`${testIdPrefix}-subtitle`}
          style={{
            margin: '4px 0 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 14.5,
            lineHeight: 1.45,
            color: 'var(--text-3)',
            fontWeight: 500,
            maxWidth: 560,
          }}
        >
          {blurb}
        </p>
      </div>

      {/* Non-commitment reassurance — this is a partnership agreement, not
          a lock-in. Owners can leave any time. Sits directly under the
          subtitle so it's the second thing they read. */}
      <div
        data-testid={`${testIdPrefix}-nonlock`}
        style={{
          margin: '14px 20px 0',
          padding: '12px 14px',
          borderRadius: 14,
          background: 'var(--accent-surface-soft)',
          border: '1px solid rgba(120, 88, 214, 0.16)',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <DoorOpen size={16} color="var(--accent-purple)" strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 800, color: 'var(--accent-purple)', letterSpacing: '0.02em' }}>
            No lock-in. You can leave any time.
          </p>
          <p style={{ margin: '3px 0 0', fontFamily: "'Inria Serif', serif", fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)' }}>
            This is a partnership agreement, not a commitment. Pause, hide your venue or delete your account whenever you want — no fees, no notice period.
          </p>
        </div>
      </div>

      {signerBlock ? (
        <div
          data-testid={`${testIdPrefix}-signer`}
          style={{
            margin: '10px 20px 0',
            padding: '12px 14px',
            borderRadius: 14,
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            boxShadow: 'var(--premium-shadow-nested)',
          }}
        >
          {signerBlock}
        </div>
      ) : null}

      <div style={{ padding: '20px 20px 0', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div
            data-testid={`${testIdPrefix}-loading`}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: 'var(--text-3)',
            }}
          >
            <Loader2 size={18} className="address-loader-spin" />
            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 14 }}>Loading agreement…</span>
          </div>
        ) : error ? (
          <p style={{ color: 'var(--status-warning-text)', fontFamily: "'Inria Serif', serif", textAlign: 'center', padding: 30 }}>
            {error}
          </p>
        ) : (
          <>
            <div
              data-testid={`${testIdPrefix}-document`}
              style={{
                flex: 1,
                background: 'var(--surface)',
                border: '1px solid var(--card-border)',
                borderRadius: 18,
                padding: 18,
                boxShadow: 'var(--premium-shadow-nested)',
                overflowY: 'auto',
                maxHeight: 'calc(100dvh - 360px)',
                minHeight: 220,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                  paddingBottom: 10,
                  borderBottom: '1px solid var(--card-border)',
                }}
              >
                <FileText size={16} color="var(--accent-purple)" strokeWidth={2.1} />
                <p
                  data-testid={`${testIdPrefix}-version`}
                  style={{
                    margin: 0,
                    fontFamily: "'Inria Serif', serif",
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--text-3)',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  Version {template.version} · {template.legal_settings?.company_name}
                </p>
              </div>
              <pre
                data-testid={`${testIdPrefix}-body`}
                style={{
                  margin: 0,
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: 'var(--text)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {reflowAgreementBody(template.body)}
              </pre>
            </div>
          </>
        )}
      </div>

      {!loading && template ? (
        <div
          style={{
            padding: '16px 20px calc(env(safe-area-inset-bottom, 0px) + 22px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, var(--app-bg) 38%)',
          }}
        >
          <label
            data-testid={`${testIdPrefix}-accept-label`}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '14px 14px',
              borderRadius: 14,
              background: 'var(--surface)',
              border: `1.5px solid ${accepted ? 'var(--accent-purple)' : 'var(--card-border)'}`,
              cursor: busy ? 'not-allowed' : 'pointer',
              transition: 'border-color 200ms ease, background 200ms ease',
            }}
          >
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              disabled={busy}
              data-testid={`${testIdPrefix}-accept-checkbox`}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--accent-purple)', cursor: busy ? 'not-allowed' : 'pointer' }}
            />
            <span
              style={{
                flex: 1,
                fontFamily: "'Inria Serif', serif",
                fontSize: 14,
                lineHeight: 1.45,
                color: 'var(--text)',
                fontWeight: 600,
              }}
            >
              I have read and agree to the {heading} (version {template.version}).
              I understand that this electronic acceptance is legally binding under
              EU eIDAS Regulation 910/2014 and Greek law.
            </span>
          </label>

          <label
            data-testid={`${testIdPrefix}-english-label`}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              padding: '14px 14px',
              borderRadius: 14,
              background: 'var(--surface)',
              border: `1.5px solid ${englishOk ? 'var(--accent-purple)' : 'var(--card-border)'}`,
              cursor: busy ? 'not-allowed' : 'pointer',
              transition: 'border-color 200ms ease, background 200ms ease',
            }}
          >
            <input
              type="checkbox"
              checked={englishOk}
              onChange={(e) => setEnglishOk(e.target.checked)}
              disabled={busy}
              data-testid={`${testIdPrefix}-english-checkbox`}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: 'var(--accent-purple)', cursor: busy ? 'not-allowed' : 'pointer' }}
            />
            <span
              style={{
                flex: 1,
                fontFamily: "'Inria Serif', serif",
                fontSize: 14,
                lineHeight: 1.45,
                color: 'var(--text)',
                fontWeight: 600,
              }}
            >
              I confirm I can read and understand English at a level sufficient to
              understand this agreement and {kind === 'venue'
                ? 'serve English-speaking guests at my venue.'
                : 'use the Allure host dashboard, which is provided in English.'}
            </span>
          </label>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid={`${testIdPrefix}-submit-button`}
            style={{
              width: '100%',
              padding: '15px 20px',
              borderRadius: 16,
              border: 'none',
              background: !canSubmit ? 'var(--accent-surface)' : 'var(--accent-purple)',
              color: !canSubmit ? 'var(--text-3)' : '#FFFFFF',
              fontFamily: "'Inria Serif', serif",
              fontSize: 16,
              fontWeight: 700,
              cursor: !canSubmit ? 'default' : 'pointer',
              boxShadow: !canSubmit ? 'none' : 'var(--premium-shadow-main)',
              transition: 'background 200ms ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {busy ? <Loader2 size={16} className="address-loader-spin" /> : <Download size={16} strokeWidth={2.2} />}
            {busy ? 'Submitting…' : actionLabel}
          </button>
          <p
            style={{
              margin: 0,
              textAlign: 'center',
              fontFamily: "'Inria Serif', serif",
              fontSize: 11.5,
              color: 'var(--text-3)',
            }}
          >
            A signed PDF copy will be available in your dashboard once you submit.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Helper that records an accepted agreement against the backend. Throws
 * on failure so the caller can surface a toast.
 */
export async function recordAgreement({
  kind,
  version,
  entity_id,
  entity_name,
  region = '',
  country = '',
  city = '',
  signer_first_name = '',
  signer_last_name = '',
  signer_business_vat = '',
  accepted_agreement = true,
  accepted_english_comprehension = false,
}) {
  const token = localStorage.getItem('token');
  const res = await axios.post(
    `${API}/agreements`,
    {
      kind,
      entity_id: entity_id || '',
      entity_name: entity_name || '',
      region: region || '',
      country: country || '',
      city: city || '',
      version,
      signer_first_name: signer_first_name || '',
      signer_last_name: signer_last_name || '',
      signer_business_vat: signer_business_vat || '',
      accepted_agreement: !!accepted_agreement,
      accepted_english_comprehension: !!accepted_english_comprehension,
    },
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  return res.data;
}

export function toastAgreementError(err) {
  const msg = err?.response?.data?.detail || 'Could not record the agreement. Please try again.';
  toast.error(msg);
}
